<template>
  <div class="mx-10 ma-5">
    <v-card flat class="pa-5">
      <div>
        <PageTitleWithSearch
          :title="'Batch'"
          :btnName="'Add New Account'"
          :btnColor="'#00B3FF'"
          :btnRoute="'/BankAccounts/create'"
          :btnPermission="'company_bank_account.create'"
          :btnEnable="true"
          :loading="loading"
          :search="search"
          @searchText="searchChange($event)"
        />
        <template v-if="filteredBatchList.length > 0">
          <DataTableWithPagination
            :headers="headers"
            :items="filteredBatchList"
            :loading="loading"
            :page="page"
            :total="total"
            @pageChange="page = $event"
          >
            <template #row="{ item, index }">
              <tr
                :key="item.batch_code"
                :class="[
                  { 'first-row': index === 0 },
                  index % 2 === 0 ? 'dark-row' : 'light-row',
                  'custom-row',
                ]"
                @click="hasPermission('company_bank_account.view') && goToTransactions(item)"
              >
                <td class="text-left">{{ item.bank }}</td>
                <td class="text-left">{{ item.branch }}</td>
                <td class="text-left">{{ item.account_number }}</td>
                <td class="text-left">{{ item.account_type }}</td>
                <td class="text-right">{{ item.starting_balance }}</td>
                <td class="text-center">
                  <v-btn
                    v-if="hasPermission('company_bank_account.edit')"
                    class="mx-3"
                    color="#FFE0B2"
                    :to="'/BankAccounts/edit/' + item.uuid"
                    fab
                    small
                    style="box-shadow: none"
                    :disabled="!item.is_active"
                  >
                    <v-icon small color="#FF9800">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    v-else
                    class="mx-3"
                    disabled
                    fab
                    x-small
                    style="box-shadow: none;"
                    ><v-icon small color="#FF9800">mdi-pencil</v-icon></v-btn
                  >
                  <!-- <v-btn
                    v-if="item.is_active && hasPermission('company_bank_account.archive')"
                    class="ma-1"
                    color="#FFCDD2"
                    fab
                    x-small
                    style="box-shadow: none"
                    @click.stop="archiveBankAccount(item.uuid)"
                  >
                    <v-icon small color="#D32F2F">mdi-archive</v-icon>
                  </v-btn> -->
                  <!-- Unarchive Button (Only for Archived Accounts) -->
                  <!-- <v-btn
                    v-if="!item.is_active && hasPermission('company_bank_account.unarchive')"
                    class="my-1"
                    color="#C8E6C9"
                    fab
                    x-small
                    style="box-shadow: none"
                    @click.stop="unarchiveBankAccount(item.uuid)"
                  >
                    <v-icon small color="#4CAF50">mdi-restore</v-icon>
                  </v-btn> -->
                </td>
              </tr>
            </template>
          </DataTableWithPagination>
        </template>
        <template v-else>
          <v-alert type="info" class="mt-5" outlined>
            No bank accounts found for the applied filters.
          </v-alert>
        </template>
      </div>
    </v-card>
  </div>
</template>

<script>
import DataTableWithPagination from "@/components/shared/DataTableWithPagination.vue";
import PageTitleWithSearch from "@/components/shared/PageTitleWithSearch";
import { CompanyBankService } from "@/services/bank-service";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  name: "batchListTable",
  components: {
    PageTitleWithSearch,
    DataTableWithPagination,
  },
  data() {
    return {
      loading: false,
      search: "",
      batchList: [],
      page: 1,
      total: 0,
      headers: [
        { text: "Bank", align: "left", value: "bank" },
        { text: "Branch", align: "left", value: "branch" },
        { text: "Account Number", align: "left", value: "account_number" },
        { text: "Account Type", align: "left", value: "account_type" },
        {
          text: "Starting Balance",
          align: "center",
          value: "starting_balance",
        },
        { text: "Actions", align: "center", value: "actions" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
    filteredBatchList() {
      return this.batchList.map((item) => ({
        ...item,
        is_active: item.is_active !== false,
      }));
    },
  },
  created() {
    this.fetchBankAccountList();
  },
  watch: {
    search() {
      this.page = 1;
      this.fetchBankAccountList();
    },
    page() {
      this.fetchBankAccountList();
    },
  },
  methods: {
    async archiveBankAccount(uuid) {
      if (!confirm("Are you sure you want to archive this bank account?")) {
        return;
      }

      this.loading = true;
      try {
        await CompanyBankService.archiveBankAccount(uuid);
        this.batchList = this.batchList.map((item) =>
          item.uuid === uuid ? { ...item, is_active: false } : item
        );
      } catch (error) {
        console.error("Error archiving bank account:", error);
      } finally {
        this.loading = false;
      }
    },

    async unarchiveBankAccount(uuid) {
      if (!confirm("Are you sure you want to unarchive this bank account?")) {
        return;
      }

      this.loading = true;
      try {
        await CompanyBankService.unarchiveBankAccount(uuid);
        this.batchList = this.batchList.map((item) =>
          item.uuid === uuid ? { ...item, is_active: true } : item
        );
      } catch (error) {
        console.error("Error unarchiving bank account:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchBankAccountList() {
      this.loading = true;
      try {
        const response = await CompanyBankService.getBankAccountList(
          this.search,
          this.page
        );
        this.batchList = (response.data?.data || []).map((item) => ({
          ...item,
          is_active: item.is_active === 1,
        }));
        this.total = response.data?.meta?.last_page || 0;
      } catch (error) {
        this.batchList = [];
        this.total = 0;
        console.error("Error fetching Batch List:", error);
      } finally {
        this.loading = false;
      }
    },

    searchChange(event) {
      this.search = event;
    },

    goToTransactions(item) {
      this.$router.push({
        name: "BankTransactions",
        params: { uuid: item.uuid },
      });
    },
  },
};
</script>

<style scoped>
.light-row {
  background-color: #ffffff;
}
.custom-row:hover {
  background-color: #e8e8e8;
}
</style>
